/**
 * Composable per gestione client status delle righe nei tab relazionali
 * Gestisce operazioni logiche add/delete/update con clientStatus
 */

export interface TabRowClientStatusOptions<T> {
	// Master entity info
	masterId: number;
	masterIdField: string; // es: 'jobOrderId'

	// Data reference (ref da useTabRelationCrud)
	itemsRef: import('vue').Ref<T[]>;

	// Data mapping (condivisa con useTabRelationCrud)
	mapForSave: (item: T) => Partial<T>;

	// Dependencies injection
	setAsDirty?: () => void;
	forceTableRerender?: () => void;
}

export function useTabRowClientStatus<T extends Record<string, any>>(
	options: TabRowClientStatusOptions<T>,
) {
	// CLIENT ADD - Aggiunge item con clientStatus: 'Nuovo'
	function clientAdd(newItemData: Partial<T>): void {
		const newItem = {
			...newItemData,
			clientStatus: 'Nuovo',
			[options.masterIdField]: options.masterId,
		} as unknown as T;

		options.itemsRef.value.push(newItem);
		if (options.forceTableRerender) {
			options.forceTableRerender();
		}

		if (options.setAsDirty) {
			options.setAsDirty();
		}
	}

	// CLIENT DELETE - Marca come eliminato o rimuove dall'array
	function clientDelete(id: number | null, index: number): void {
		if (id) {
			// Logical delete - marca come eliminato
			options.itemsRef.value[index] = {
				...options.itemsRef.value[index],
				toBeDeleted: true,
				clientStatus: 'Eliminato',
				modified: true,
			};
		} else {
			// Physical delete - rimuovi dall'array (item senza id)
			options.itemsRef.value.splice(index, 1);
		}

		if (options.forceTableRerender) {
			options.forceTableRerender();
		}

		if (options.setAsDirty) {
			options.setAsDirty();
		}
	}

	// CLIENT UPDATE - Marca come modificato
	function clientUpdate(index: number, updatedData: Partial<T>): void {
		const currentItem = options.itemsRef.value[index];
		options.itemsRef.value[index] = {
			...currentItem,
			...updatedData,
			modified: true,
			clientStatus: currentItem.id ? 'Modificato' : 'Nuovo', // Mantiene 'Nuovo' se non ha id
		};

		if (options.forceTableRerender) {
			options.forceTableRerender();
		}

		if (options.setAsDirty) {
			options.setAsDirty();
		}
	}

	return {
		clientAdd,
		clientDelete,
		clientUpdate,
	};
}
